// Confirm a bank credit as a card/mada settlement → clears that branch's Card Clearing (existing manual-match endpoint).
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CreditCard, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function CardSettlementPanel({ statementId, txnIdx, deposit, onDone }) {
  const [branches, setBranches] = useState([]);
  const [f, setF] = useState({ branch: '', fee: '', vat: '', ref: '', provider: '', terminal: '', ok: false });
  const [split, setSplit] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/bank-statements/card-clearing/open').then(({ data }) => setBranches(data.branches || [])).catch(() => setBranches([]));
  }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const gross = Number(deposit || 0) + Number(f.fee || 0) + Number(f.vat || 0);
  const open = branches.find((b) => b.branch_id === f.branch)?.open ?? 0;
  const allocations = Object.entries(split).filter(([, v]) => Number(v) > 0).map(([branch_id, v]) => ({ branch_id, gross: Number(v) }));
  const splitMode = allocations.length > 0;
  const splitTotal = allocations.reduce((s, a) => s + a.gross, 0);
  const splitOk = !splitMode || Math.abs(splitTotal - gross) < 0.01;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bank-statements/${statementId}/manual-match`, {
        txn_index: txnIdx, match_type: 'card_settlement', match_id: splitMode ? allocations[0].branch_id : f.branch,
        allocations: splitMode ? allocations : undefined, provider: f.provider || undefined, terminal_ref: f.terminal || undefined,
        fee: Number(f.fee || 0), fee_vat: Number(f.vat || 0), fee_vat_invoice_ref: f.ref || undefined,
      });
      toast.success(`Card settlement posted — ${fmt(data.match_amount)} cleared, ${fmt(data.open_after)} still open`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Settlement failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid="card-settlement-panel">
      <p className="text-sm font-semibold flex items-center gap-2"><CreditCard size={14} className="text-indigo-600" /> Card / mada settlement</p>
      <select value={f.branch} onChange={set('branch')} className="w-full h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="card-settlement-branch">
        <option value="">Terminal branch (whose card sales are settled)…</option>
        {branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name} — open {fmt(b.open)}</option>)}
      </select>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Provider (e.g. Geidea, mada)" value={f.provider} onChange={set('provider')} data-testid="card-settlement-provider" />
        <Input placeholder="Terminal / settlement ref" value={f.terminal} onChange={set('terminal')} data-testid="card-settlement-terminal" />
      </div>
      <SplitRows branches={branches} split={split} setSplit={setSplit} gross={gross} total={splitTotal} />
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" min="0" step="0.01" placeholder="Fee deducted" value={f.fee} onChange={set('fee')} data-testid="card-settlement-fee" />
        <Input type="number" min="0" step="0.01" placeholder="VAT on fee" value={f.vat} onChange={set('vat')} data-testid="card-settlement-fee-vat" />
        <Input placeholder="Tax invoice ref" value={f.ref} onChange={set('ref')} disabled={!Number(f.vat)} data-testid="card-settlement-vat-ref" />
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="card-settlement-summary">
        Deposit {fmt(deposit)} + fee {fmt(f.fee)} + VAT {fmt(f.vat)} = clears {fmt(gross)}{f.branch ? ` of ${fmt(open)} open` : ''}. Leave fee empty if fees are booked separately.
      </p>
      <label className="flex items-center gap-2 text-xs">
        <Checkbox checked={f.ok} onCheckedChange={(v) => setF((p) => ({ ...p, ok: !!v }))} data-testid="card-settlement-confirm-check" />
        I confirm this bank credit is a card/mada settlement
      </label>
      <Button size="sm" className="rounded-xl" disabled={!f.ok || (!splitMode && !f.branch) || !splitOk || busy || (Number(f.vat) > 0 && !f.ref)} onClick={submit} data-testid="card-settlement-submit">
        {busy && <Loader2 size={13} className="animate-spin mr-1" />} Confirm settlement
      </Button>
    </div>
  );
}

function SplitRows({ branches, split, setSplit, gross, total }) {
  const [show, setShow] = useState(false);
  if (!show) {
    return <button type="button" className="text-[11px] text-indigo-700 underline" onClick={() => setShow(true)} data-testid="card-settlement-split-toggle">Combined deposit for several branches?</button>;
  }
  return (
    <div className="space-y-1 rounded-lg border border-indigo-100 bg-white/60 p-2" data-testid="card-settlement-split">
      {branches.map((b) => (
        <div key={b.branch_id} className="flex items-center gap-2 text-xs">
          <span className="flex-1 truncate">{b.branch_name} <span className="text-muted-foreground">open {fmt(b.open)}</span></span>
          <Input type="number" min="0" step="0.01" className="h-8 w-28" value={split[b.branch_id] || ''}
            onChange={(e) => setSplit((p) => ({ ...p, [b.branch_id]: e.target.value }))} data-testid={`card-settlement-split-${b.branch_id}`} />
        </div>
      ))}
      <p className={`text-[11px] ${Math.abs(total - gross) < 0.01 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="card-settlement-split-total">
        Allocated {fmt(total)} of {fmt(gross)} (deposit + fee + VAT)
      </p>
    </div>
  );
}
